import styles from "./interest.module.css"
import React from "react"
import * as util from "activities/util"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import * as callouts from "activities/components/callouts"
import * as finance from "shared/finance"
import * as graphUtil from "shared/graphs/util"
import Line from "story/components/graphs/line"
import AccessibilityTable from "activities/components/shared/accessibility-table"

/* Data for graph format:
[
[year, simpleInterest, compoundInterest],
[year, simpleInterest, compoundInterest],
[year, simpleInterest, compoundInterest]
]
*/

const genInterestData = (principal, years, rate) =>
  util.xAxisArray(years).reduce((memo, years, idx) => {
    memo.push([years, finance.amountWithSimpleInterestPrincipal(principal, years, rate), finance.amountWithCompoundInterest(principal, years, rate)])
    return memo
  }, [])

const calcCallouts = ({principal, years, rate}) => {
  const decimalRate = rate / 100
  return {
    simpleInterest: finance.simpleInterest(principal, years, decimalRate),
    compoundInterest: finance.interestEarnedOnPrincipal(principal, years, decimalRate),
    endingBalance: finance.amountWithCompoundInterest(principal, years, decimalRate)
  }
}

export default shared.ActivityFactory(
  ({getProps, getState, updateState}) => {
    const state = getState()
    const {principal, years, rate, resetKey} = state
    const {id, theme} = getProps()
    const {simpleInterest, compoundInterest, endingBalance} = calcCallouts(state)

    const accessibilityTableHeadings = [
      {name: "Years", key: "year"},
      {name: "Simple Interest", key: "simpleInterest"},
      {name: "Compound Interest", key: "compoundInterest"}
    ]

    const accessibilityTableData = genInterestData(principal, years, rate / 100).map((row) => ({
      year: row[0],
      simpleInterest: util.formatDollarsNoCents(row[1]),
      compoundInterest: util.formatDollarsNoCents(row[2])
    }))

    const accessibilityTableLabel = "Simple VS Compound Table"

    return (
      <div>
        <shared.Content>
          <shared.SummaryCalcContainer>
            <shared.InputsContainer>
              <inputs.Input id={id} size="big" title="Starting Balance" value={principal} onChange={updateState("principal", util.fromString)} />
              <inputs.RangeInput
                id={id}
                title="Interest Rate"
                onChange={updateState("rate", parseFloat)}
                initialValue={rate}
                min={0.25}
                max={15}
                step={0.25}
                units="%"
                valueType="percent"
                resetKey={resetKey}
              />
              <inputs.RangeInput
                id={id}
                title="Years"
                onChange={updateState("years", util.fromString)}
                initialValue={years}
                min={5}
                max={50}
                step={5}
                units="years"
                valueType="years-full"
                resetKey={resetKey}
              />
            </shared.InputsContainer>
            <shared.ChartContainer>
              <Line
                ariaLabel={"Chart that demonstrates the difference between simple and compound interest over time."}
                values={genInterestData(principal, years, rate / 100)}
                yAxis="Balance"
                formatter="currency"
                tooltip={[
                  "Year",
                  {name: "With Simple Interest", type: "currency", color: "#5C5761"},
                  {name: "With Compound Interest", type: "currency", color: graphUtil.getColor(theme, 1)}
                ]}
                customTooltipStyles={{width: 281}}
                type="calculator"
                colors={graphUtil.colorsFor2Lines(theme)}
              />
            </shared.ChartContainer>
            <shared.SummaryContainer className={styles["summary"]}>
              <callouts.Callout>
                <callouts.Detail label="Simple Interest" result={util.formatDollarsNoCents(util.ifNegative(simpleInterest))} dataTestId="simple-interest" />
                <callouts.Detail label="Compound Interest" result={util.formatDollarsNoCents(util.ifNegative(compoundInterest))} dataTestId="compound-interest" />
                <callouts.Detail label="Ending Balance" result={util.formatDollarsNoCents(endingBalance)} dataTestId="ending-balance" />
              </callouts.Callout>
            </shared.SummaryContainer>
          </shared.SummaryCalcContainer>
        </shared.Content>

        <AccessibilityTable headings={accessibilityTableHeadings} data={accessibilityTableData} label={accessibilityTableLabel} />
      </div>
    )
  },
  {
    stateFilter: (state) => {
      const {simpleInterest, compoundInterest, endingBalance} = calcCallouts(state)
      return Object.assign(state, {
        simpleInterest: util.round(simpleInterest),
        compoundInterest: util.round(compoundInterest),
        endingBalance: util.round(endingBalance)
      })
    },
    defaultProps: {
      id: "interest-calculator",
      className: "interest-calculator",
      title: "Simple VS Compound Interest",
      subtitle: "Explore how your savings grow differently with simple or compound interest."
    },
    defaultState: {
      principal: 10000,
      years: 15,
      rate: 4
    }
  }
)
